import { Box, Button, IconButton, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import { useComputedCompany } from "Components/Company/hooks/useComputedCompany";
import { color, px } from "Configs/theme";
import { getColorRgbaFromHex } from "Helpers/ColorHelper";
import { ScheduleGroupItem } from "domain-entities";
import React, { FC, useMemo, useRef } from "react";
import { GroupOptions, ReactSortable, Sortable } from "react-sortablejs";
import { ScheduleGroupItemName } from "./ScheduleGroupItemName";

export const ANIMATION_DURATION = 150;

interface Props {
	name: string;
	items: ScheduleGroupItem[];
	group: GroupOptions;
	setList: (nextItems: ScheduleGroupItem[], sortable: Sortable | null) => void;
	emptyPlaceholder: string;
	isSortable?: boolean;
	onClickEdit?: () => void;
	actionLabel?: string;
	onAction?: () => void;
	onItemAction?: (scheduleGroupItem: ScheduleGroupItem) => void;
	isItemActionEdit?: boolean;
}

const ScheduleGroupItemList: FC<Props> = (props) => {
	const { onItemAction } = props;
	const isFirstRender = useRef(true);
	const validMembers = useComputedCompany((state) => state.validMembers);
	const filteredItems = useMemo(() => {
		return props.items.filter(
			(item) =>
				!item.userId || validMembers.some((member) => member.id === item.userId)
		);
	}, [props.items, validMembers]);

	const classes = useStyles({
		groupName: props.group.name,
		emptyPlaceholder: props.emptyPlaceholder,
	});

	return (
		<Box className={classes.root}>
			<Box
				className={classes.header}
				style={{ cursor: props.isSortable ? "grab" : "inherit" }}
			>
				<Box className={classes.groupName}>{props.name}</Box>
				{props.onClickEdit && (
					<IconButton className="edit" size="small" onClick={props.onClickEdit}>
						<EditIcon htmlColor={color.totalBackground} fontSize="inherit" />
					</IconButton>
				)}
				{props.onAction && props.actionLabel && (
					<Button
						className={classes.actionButton}
						color="primary"
						onClick={props.onAction}
					>
						+ {props.actionLabel}
					</Button>
				)}
			</Box>
			<Box className={classes.items}>
				<ReactSortable
					tag="div"
					list={filteredItems}
					group={props.group}
					animation={ANIMATION_DURATION}
					swapThreshold={0.8}
					ghostClass={classes.ghost}
					setList={(nextItems, sortable) => {
						if (isFirstRender.current) {
							isFirstRender.current = false;
							setTimeout(() => props.setList(nextItems, sortable));
						} else {
							props.setList(nextItems, sortable);
						}
					}}
				>
					{filteredItems.map((item) => (
						<Box key={item.id} className={classes.outerItem}>
							<Box key={item.id} className={classes.innerItem}>
								<Box className={classes.itemName}>
									<ScheduleGroupItemName scheduleGroupItem={item} />
								</Box>
								{onItemAction && (
									<IconButton
										className="action"
										size="small"
										onClick={() => onItemAction(item)}
									>
										{props.isItemActionEdit ? (
											<EditIcon htmlColor={color.text} fontSize="inherit" />
										) : (
											<CloseIcon htmlColor={color.text} fontSize="inherit" />
										)}
									</IconButton>
								)}
							</Box>
						</Box>
					))}
				</ReactSortable>
			</Box>
		</Box>
	);
};

export default ScheduleGroupItemList;

interface StyleProps {
	groupName: string;
	emptyPlaceholder: string;
}

const useStyles = makeStyles({
	root: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderRadius: `${px.xs} ${px.xs} 0 0`,
		backgroundColor: color.text,
		color: color.totalBackground,
		textTransform: "uppercase",
		fontSize: "14px",
		fontWeight: "bold",
		letterSpacing: 0,
		"& > .edit": {
			marginRight: px.l,
			display: "none",
		},
		"&:hover > .edit": {
			display: "block",
		},
	},
	actionButton: {
		marginRight: px.s,
	},
	groupName: {
		padding: `${px.m} ${px.l}`,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	items: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		flex: 1,
		backgroundColor: color.toggleBackground,
		"& > div": {
			display: "flex",
			flexDirection: "column",
			width: "100%", // needed for smaller screens
			flex: 1,
			"&:empty": {
				height: 68,
				flex: props.groupName === "items" ? "none" : 1,
				"&::after": {
					display: "flex",
					flex: 1,
					alignItems: "center",
					justifyContent: "center",
					padding: `0 ${px.l}`,
					textAlign: "center",
					fontSize: "14px",
					letterSpacing: 0,
					color: getColorRgbaFromHex(color.text, 0.4),
					content: `"${props.emptyPlaceholder.replace(/"/g, '\\"')}"`,
				},
			},
		},
	}),
	outerItem: {
		padding: `${px.xxs} ${px.l}`,
		"&:first-child": {
			paddingTop: px.l,
		},
		"&:last-child": {
			paddingBottom: px.l,
		},
	},
	innerItem: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		cursor: "grab",
		borderRadius: px.xs,
		backgroundColor: color.totalBackground,
		whiteSpace: "nowrap",
		overflow: "hidden",
		fontSize: "14px",
		fontWeight: "bold",
		letterSpacing: 0,
		"& > .action": {
			marginRight: px.s,
			display: "none",
		},
		"&:hover > .action": {
			display: "block",
		},
	},
	itemName: {
		padding: `${px.s} ${px.m}`,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	ghost: {
		"& > div": {
			backgroundColor: getColorRgbaFromHex(color.primary, 0.12),
			"& > *": {
				visibility: "hidden",
			},
		},
	},
});
